"use client"

import { useState, useCallback, useEffect } from 'react'
import { S3Client, ListObjectsV2Command, ListObjectsV2CommandOutput } from '@aws-sdk/client-s3'

/**
 * Types for S3-compatible file picker component
 */

export interface S3Object {
  key: string
  size?: number
  lastModified?: Date
  etag?: string
  type: 'file' | 'folder'
  name: string
}

export interface S3BucketConfig {
  bucket: string
  region: string
  endpoint?: string
  accessKeyId: string
  secretAccessKey: string
  forcePathStyle?: boolean
}

export interface S3PickerConfig {
  /**
   * S3 bucket configuration
   */
  config: S3BucketConfig
  
  /**
   * Initial prefix/folder to start browsing from
   */
  initialPrefix?: string
  
  /**
   * Whether to allow multiple file selection
   */
  multiselect?: boolean
  
  /**
   * Allowed file extensions (e.g., ['.csv', '.json'])
   */
  allowedExtensions?: string[]
  
  /**
   * MIME types to filter by
   */
  mimeTypes?: string[]
  
  /**
   * Whether to show folders (default: true)
   */
  showFolders?: boolean
  
  /**
   * Maximum number of objects to display per page
   */
  pageSize?: number
  
  /**
   * Whether to show file sizes
   */
  showFileSize?: boolean
  
  /**
   * Whether to show last modified dates
   */
  showLastModified?: boolean
}

export interface S3PickerProps extends S3PickerConfig {
  /**
   * Callback when files are selected
   */
  onFilesSelected: (files: S3Object[]) => void
  
  /**
   * Optional callback when selection changes
   */
  onSelectionChange?: (selectedFiles: S3Object[]) => void
  
  /**
   * Whether the picker is in loading state
   */
  isLoading?: boolean
  
  /**
   * Custom CSS classes
   */
  className?: string
  
  /**
   * Whether the picker is disabled
   */
  disabled?: boolean
}

export interface S3NavigationState {
  currentPrefix: string
  breadcrumbs: Array<{ name: string; prefix: string }>
  objects: S3Object[]
  selectedFiles: S3Object[]
  isLoading: boolean
  error: string | null
  hasMore: boolean
  nextToken?: string
}

export interface S3PickerActions {
  navigateToFolder: (prefix: string) => Promise<void>
  navigateUp: () => Promise<void>
  navigateToRoot: () => Promise<void>
  selectFile: (file: S3Object) => void
  deselectFile: (file: S3Object) => void
  toggleFileSelection: (file: S3Object) => void
  clearSelection: () => void
  loadMore: () => Promise<void>
  refresh: () => Promise<void>
}

export interface UseS3PickerReturn {
  state: S3NavigationState
  actions: S3PickerActions
}

/**
 * Hook for managing S3 file picker state and operations
 */
export function useS3Picker({
  config,
  initialPrefix = '',
  multiselect = false,
  allowedExtensions,
  showFolders = true,
  pageSize = 100
}: S3PickerConfig): UseS3PickerReturn {
  const [state, setState] = useState<S3NavigationState>({
    currentPrefix: initialPrefix,
    breadcrumbs: initialPrefix ? parseBreadcrumbs(initialPrefix) : [{ name: 'Root', prefix: '' }],
    objects: [],
    selectedFiles: [],
    isLoading: false,
    error: null,
    hasMore: false,
    nextToken: undefined
  })

  // Create S3 client
  const s3Client = new S3Client({
    region: config.region,
    endpoint: config.endpoint,
    credentials: {
      accessKeyId: config.accessKeyId,
      secretAccessKey: config.secretAccessKey
    },
    forcePathStyle: config.forcePathStyle ?? true
  })

  // Parse prefix into breadcrumb trail
  function parseBreadcrumbs(prefix: string): Array<{ name: string; prefix: string }> {
    const breadcrumbs = [{ name: 'Root', prefix: '' }]
    
    if (!prefix) return breadcrumbs
    
    const parts = prefix.split('/').filter(part => part)
    let currentPrefix = ''
    
    for (const part of parts) {
      currentPrefix += part + '/'
      breadcrumbs.push({
        name: part,
        prefix: currentPrefix
      })
    }
    
    return breadcrumbs
  }

  // Convert S3 response to our S3Object format
  function parseS3Objects(
    response: ListObjectsV2CommandOutput,
    prefix: string
  ): S3Object[] {
    const objects: S3Object[] = []
    
    // Add folders (common prefixes)
    if (showFolders && response.CommonPrefixes) {
      for (const commonPrefix of response.CommonPrefixes) {
        if (commonPrefix.Prefix && commonPrefix.Prefix !== prefix) {
          const folderName = commonPrefix.Prefix
            .slice(prefix.length)
            .replace(/\/$/, '')
          
          objects.push({
            key: commonPrefix.Prefix,
            type: 'folder',
            name: folderName
          })
        }
      }
    }
    
    // Add files
    if (response.Contents) {
      for (const obj of response.Contents) {
        if (obj.Key && obj.Key !== prefix && !obj.Key.endsWith('/')) {
          const fileName = obj.Key.slice(prefix.length)
          
          // Filter by allowed extensions if specified
          if (allowedExtensions && allowedExtensions.length > 0) {
            const extension = '.' + fileName.split('.').pop()?.toLowerCase()
            if (!allowedExtensions.includes(extension)) {
              continue
            }
          }
          
          objects.push({
            key: obj.Key,
            type: 'file',
            name: fileName,
            size: obj.Size,
            lastModified: obj.LastModified,
            etag: obj.ETag
          })
        }
      }
    }
    
    return objects.sort((a, b) => {
      // Folders first, then files
      if (a.type !== b.type) {
        return a.type === 'folder' ? -1 : 1
      }
      // Alphabetical within type
      return a.name.localeCompare(b.name)
    })
  }

  // Load objects for current prefix
  const loadObjects = useCallback(async (
    prefix: string, 
    append = false, 
    continuationToken?: string
  ) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))
    
    try {
      const command = new ListObjectsV2Command({
        Bucket: config.bucket,
        Prefix: prefix,
        Delimiter: '/',
        MaxKeys: pageSize,
        ContinuationToken: continuationToken
      })
      
      const response = await s3Client.send(command)
      const newObjects = parseS3Objects(response, prefix)
      
      setState(prev => ({
        ...prev,
        objects: append ? [...prev.objects, ...newObjects] : newObjects,
        hasMore: !!response.IsTruncated,
        nextToken: response.NextContinuationToken,
        isLoading: false
      }))
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load objects'
      setState(prev => ({
        ...prev,
        error: errorMessage,
        isLoading: false
      }))
    }
  }, [config.bucket, pageSize, s3Client, allowedExtensions, showFolders])

  // Actions
  const navigateToFolder = useCallback(async (prefix: string) => {
    setState(prev => ({
      ...prev,
      currentPrefix: prefix,
      breadcrumbs: parseBreadcrumbs(prefix),
      selectedFiles: []
    }))
    await loadObjects(prefix)
  }, [loadObjects])

  const navigateUp = useCallback(async () => {
    const parts = state.currentPrefix.split('/').filter(part => part)
    parts.pop() // Remove last part
    const newPrefix = parts.length > 0 ? parts.join('/') + '/' : ''
    await navigateToFolder(newPrefix)
  }, [state.currentPrefix, navigateToFolder])

  const navigateToRoot = useCallback(async () => {
    await navigateToFolder('')
  }, [navigateToFolder])

  const selectFile = useCallback((file: S3Object) => {
    setState(prev => {
      if (file.type !== 'file') return prev
      
      const isAlreadySelected = prev.selectedFiles.some(f => f.key === file.key)
      if (isAlreadySelected) return prev
      
      const newSelection = multiselect 
        ? [...prev.selectedFiles, file]
        : [file]
      
      return {
        ...prev,
        selectedFiles: newSelection
      }
    })
  }, [multiselect])

  const deselectFile = useCallback((file: S3Object) => {
    setState(prev => ({
      ...prev,
      selectedFiles: prev.selectedFiles.filter(f => f.key !== file.key)
    }))
  }, [])

  const toggleFileSelection = useCallback((file: S3Object) => {
    if (file.type !== 'file') return
    
    const isSelected = state.selectedFiles.some(f => f.key === file.key)
    if (isSelected) {
      deselectFile(file)
    } else {
      selectFile(file)
    }
  }, [state.selectedFiles, selectFile, deselectFile])

  const clearSelection = useCallback(() => {
    setState(prev => ({ ...prev, selectedFiles: [] }))
  }, [])

  const loadMore = useCallback(async () => {
    if (state.hasMore && state.nextToken) {
      await loadObjects(state.currentPrefix, true, state.nextToken)
    }
  }, [state.hasMore, state.nextToken, state.currentPrefix, loadObjects])

  const refresh = useCallback(async () => {
    setState(prev => ({ ...prev, selectedFiles: [] }))
    await loadObjects(state.currentPrefix)
  }, [state.currentPrefix, loadObjects])

  // Load initial objects
  useEffect(() => {
    loadObjects(initialPrefix)
  }, []) // Only run on mount

  const actions: S3PickerActions = {
    navigateToFolder,
    navigateUp,
    navigateToRoot,
    selectFile,
    deselectFile,
    toggleFileSelection,
    clearSelection,
    loadMore,
    refresh
  }

  return { state, actions }
}